<template>
  <div
    v-loading="loading"
    class="data-source-page"
    :class="{ 'data-source-page--scroll': !loading && config }"
  >
    <p v-if="!loading && error" class="err">{{ error }}</p>
    <template v-else-if="config">
      <EntityInfoCard title="读取模式">
        <EntityInfoItem label="模式" :value="modeLabel" />
        <EntityInfoItem v-if="config.mode === 'import_file'" label="IMPORT_FILE">
          <span class="mono" :title="config.importFile || ''">{{ config.importFile || '—' }}</span>
        </EntityInfoItem>
        <EntityInfoItem v-else label="SHARE_ROOT">
          <span class="mono" :title="config.shareRoot || ''">{{ config.shareRoot || '未配置' }}</span>
        </EntityInfoItem>
        <EntityInfoItem label="SHARE_UNC（挂载）" :value="boolLabel(config.shareUncConfigured)" />
        <EntityInfoItem label="SHARE_USER" :value="boolLabel(config.shareUserConfigured)" />
        <EntityInfoItem label="SHARE_PASSWORD" :value="boolLabel(config.sharePasswordConfigured)" />
      </EntityInfoCard>

      <section class="data-source-section">
        <div class="section-head">
          <h3>已注册工作簿模板</h3>
          <p>扫描时按文件名规则认领；未注册的文件会被跳过。</p>
        </div>
        <el-table :data="config.templates" size="small" border>
          <el-table-column prop="label" label="名称" min-width="160" />
          <el-table-column prop="id" label="模板 ID" width="160" />
          <el-table-column prop="fileNamePattern" label="文件名规则" min-width="220" />
        </el-table>
      </section>

      <section class="data-source-section">
        <div class="section-head section-head--actions">
          <div>
            <h3>扫描与同步</h3>
            <p>测试扫描只检查路径，不读表、不落库。立即同步会解析并写入数据库。</p>
          </div>
          <div class="section-actions">
            <el-button size="small" icon="el-icon-search" :loading="scanning" @click="onScan">
              测试扫描
            </el-button>
            <el-button type="primary" size="small" icon="el-icon-refresh" :loading="syncing" @click="onSync">
              立即同步
            </el-button>
          </div>
        </div>
        <p v-if="syncMessage" class="sync-message" :class="{ 'is-error': syncOk === false }">{{ syncMessage }}</p>
        <div v-if="syncFiles.length" class="sync-table-wrap">
          <el-table :data="syncFiles" size="small" border max-height="280">
            <el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip />
            <el-table-column label="年份" width="72" align="center" :formatter="formatSyncGroupKey" />
            <el-table-column label="结果" width="128" align="center" :formatter="formatSyncStatus" />
            <el-table-column
              label="失败原因"
              min-width="160"
              show-overflow-tooltip
              :formatter="formatSyncErrorMessage"
            />
            <el-table-column prop="rowCount" label="有效行数" width="88" align="center" />
          </el-table>
        </div>
        <p v-if="scanMessage" class="scan-message" :class="{ 'is-error': scanOk === false }">{{ scanMessage }}</p>
        <div v-if="scanFiles.length" class="scan-table-wrap">
          <el-table :data="scanFiles" size="small" border max-height="360">
            <el-table-column prop="templateLabel" label="模板" width="140" />
            <el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip />
            <el-table-column prop="filePath" label="路径" min-width="240" show-overflow-tooltip />
            <el-table-column label="修改时间" width="158" :formatter="formatScanModifiedAt" />
            <el-table-column label="导入" width="88" align="center" :formatter="formatScanSelected" />
            <el-table-column
              prop="skipReason"
              label="说明"
              min-width="140"
              show-overflow-tooltip
              :formatter="formatScanSkipReason"
            />
          </el-table>
        </div>
      </section>

      <Crud class="list-page-crud" :pagination="batchPagination">
        <template #header>
          <div class="section-head section-head--flush">
            <h3>同步任务</h3>
            <p>
              全部导入记录（成功与失败），按完成时间倒序；「投诉明细当前」为投诉明细页展示的年份最新成功导入。
              立即同步时若源文件未改而跳过，不会新增记录，请查看上方「扫描与同步」中的当次结果表。
            </p>
          </div>
        </template>
        <template #table>
          <Table
            :columns="batchTableColumns"
            :data="batches"
            :loading="batchesLoading"
            row-key="id"
            border
            height="100%"
            size="small"
          />
        </template>
      </Crud>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import {
  type ImportSourceConfig,
  type ScannedWorkbook,
  type ImportBatchSummary,
  type SyncFileResult,
  type SyncFileStatus,
  fetchBatches,
  fetchImportSource,
  runImport,
  scanImportSource,
} from '@/api/import';
import { EntityInfoCard, EntityInfoItem } from '@/components';
import { Crud, Table } from '@/components/crud';
import type { ColumnSchema } from '@/components/crud/types';
import { formatDisplay, type TimeInput } from '@/utils/time';
import { importBatchColumns } from '@/views/data-ops/complaint-columns';

const DEFAULT_BATCH_PAGE_SIZE = 20;

export default Vue.extend({
  name: 'DataSource',
  components: { Crud, EntityInfoCard, EntityInfoItem, Table },
  data() {
    return {
      loading: true,
      error: '' as string,
      config: null as ImportSourceConfig | null,
      scanning: false,
      scanMessage: '' as string,
      scanOk: null as boolean | null,
      scanFiles: [] as ScannedWorkbook[],
      syncing: false,
      syncMessage: '' as string,
      syncOk: null as boolean | null,
      syncFiles: [] as SyncFileResult[],
      batchesLoading: false,
      batches: [] as ImportBatchSummary[],
      batchTotal: 0,
      batchPage: 1,
      batchPageSize: DEFAULT_BATCH_PAGE_SIZE,
    };
  },
  computed: {
    batchTableColumns(): ColumnSchema[] {
      return importBatchColumns.map((schema) => {
        if (schema.type !== 'custom' || schema.prop !== 'remark') return schema;
        const { label, minWidth } = schema;
        return {
          type: 'text',
          prop: 'remark',
          label,
          minWidth,
          align: 'left',
          formatter: (_value, row) => this.batchRemarkText(row as ImportBatchSummary),
        };
      });
    },
    modeLabel(): string {
      if (!this.config) return '—';
      return this.config.mode === 'import_file' ? '单文件（IMPORT_FILE）' : '目录扫描（SHARE_ROOT）';
    },
    batchPagination(): {
      page: number;
      pageSize: number;
      total: number;
      layout: string;
      pageSizes: number[];
      onPageChange: (page: number) => void;
      onSizeChange: (size: number) => void;
    } {
      return {
        page: this.batchPage,
        pageSize: this.batchPageSize,
        total: this.batchTotal,
        layout: 'total, sizes, prev, pager, next, jumper',
        pageSizes: [10, 20, 50, 100],
        onPageChange: (page: number) => {
          this.batchPage = page;
          this.loadBatches();
        },
        onSizeChange: (size: number) => {
          this.batchPageSize = size;
          this.batchPage = 1;
          this.loadBatches();
        },
      };
    },
  },
  created() {
    this.loadConfig();
    this.loadBatches();
  },
  methods: {
    loadBatches() {
      this.batchesLoading = true;
      return fetchBatches({ page: this.batchPage, pageSize: this.batchPageSize })
        .then((payload) => {
          this.batches = payload.items;
          this.batchTotal = payload.total;
        })
        .catch(() => {
          this.batches = [];
          this.batchTotal = 0;
        })
        .then(() => {
          this.batchesLoading = false;
        });
    },
    syncStatusLabel(status: SyncFileStatus): string {
      if (status === 'imported') return '新导入';
      if (status === 'skipped') return '文件未改而跳过';
      return '失败';
    },
    formatSyncGroupKey(row: SyncFileResult): string {
      return row.groupKey ? `${row.groupKey}年` : '—';
    },
    formatSyncStatus(row: SyncFileResult): string {
      const status = row.status;
      if (status === 'imported') return '新导入';
      if (status === 'skipped') return '文件未改而跳过';
      return '失败';
    },
    formatSyncErrorMessage(row: SyncFileResult): string {
      return row.errorMessage || '—';
    },
    formatScanModifiedAt(row: ScannedWorkbook): string {
      return formatDisplay(row.modifiedAt, 'datetimeSec');
    },
    formatScanSelected(row: ScannedWorkbook): string {
      return row.selected ? '是' : '否';
    },
    formatScanSkipReason(row: ScannedWorkbook): string {
      return row.skipReason || (row.selected ? '将参与导入' : '—');
    },
    batchRemarkText(row: ImportBatchSummary): string {
      if (row.isCurrent) {
        return `投诉明细当前${row.groupKey ? `（${row.groupKey}年）` : ''}`;
      }
      if (row.errorMessage) return row.errorMessage;
      return '—';
    },
    loadConfig() {
      this.loading = true;
      fetchImportSource()
        .then((payload) => {
          this.config = payload;
          this.error = '';
        })
        .catch((err: Error) => {
          this.error = err.message || '加载失败';
        })
        .then(() => {
          this.loading = false;
        });
    },
    boolLabel(ok: boolean): string {
      return ok ? '已配置' : '未配置';
    },
    formatTime(value: TimeInput): string {
      return formatDisplay(value, 'datetimeSec');
    },
    onScan() {
      this.scanning = true;
      this.scanMessage = '';
      this.scanOk = null;
      scanImportSource()
        .then((result) => {
          this.scanOk = result.ok;
          this.scanMessage = result.message;
          this.scanFiles = result.files;
          if (!result.ok) this.$message.warning(result.message);
          else if (!result.files.length) this.$message.info(result.message);
          else this.$message.success(result.message);
        })
        .catch((err: Error) => {
          this.scanOk = false;
          this.scanMessage = err.message || '扫描失败';
          this.scanFiles = [];
          this.$message.error(this.scanMessage);
        })
        .then(() => {
          this.scanning = false;
        });
    },
    onSync() {
      if (this.syncing) return;
      this.$confirm(
        '将扫描数据源目录并导入匹配的投诉跟进表，文件未改则跳过，不回写 xls。',
        '立即同步',
        {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        },
      )
        .then(() => {
          this.syncing = true;
          this.syncMessage = '';
          this.syncOk = null;
          this.syncFiles = [];
          return runImport();
        })
        .then((result) => {
          if (!result) return;
          this.syncOk = result.ok;
          this.syncMessage = result.message;
          this.syncFiles = result.files || [];
          if (result.ok && !result.skipped) this.$message.success(result.message);
          else if (result.skipped) this.$message.info(result.message);
          else this.$message.error(result.message || '同步失败');
          this.batchPage = 1;
          return this.loadBatches();
        })
        .catch((err: Error | string) => {
          if (err === 'cancel' || err === 'close') return;
          this.syncOk = false;
          this.syncMessage = err instanceof Error ? err.message : '同步失败';
          this.syncFiles = [];
          this.$message.error(this.syncMessage);
        })
        .then(() => {
          this.syncing = false;
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.data-source-page {
  @include list-page-layout;
  position: relative;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: $color-text;

  &--scroll {
    flex: 0 0 auto;
    height: auto;
    min-height: 0;
  }

  > :not(.list-page-crud) {
    flex-shrink: 0;
  }
}

.err {
  color: $color-danger;
  font-size: 14px;
}

.data-source-section {
  flex-shrink: 0;
  padding: 14px 16px;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
}

.section-head {
  margin-bottom: 12px;

  &--flush {
    margin-bottom: 0;
  }

  h3 {
    margin: 0 0 4px;
    font-size: 13px;
    font-weight: 600;
    color: $color-text;
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    color: $color-text-secondary;
  }
}

.section-head--actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.scan-message {
  margin: 0 0 10px;
  font-size: 13px;
  color: $color-text-regular;

  &.is-error {
    color: $color-danger;
  }
}

.section-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

.sync-message {
  margin: 0 0 10px;
  font-size: 13px;
  color: $color-text-regular;

  &.is-error {
    color: $color-danger;
  }
}

.scan-table-wrap,
.sync-table-wrap {
  min-height: 120px;
}

.mono {
  font-size: 13px;
  word-break: break-all;
}
</style>
